import {mergeWith} from 'lodash';

import {logger} from '../../../../srcCommon/services/logger/logger';
import {getTimestampFromCounterUid} from '../../../helpers/counterHelpers';
import {BadgeCountersModel, BoldCountersModel, CountersModel} from '../../../models/api/countersModel';
import {AppAction} from '../../appActions';
import {DataActionsEnum} from '../dataActions';
import {CountersActionsEnum} from './countersActions';

export function countersReducer(state: CountersModel, action: AppAction): CountersModel {
  switch (action.type) {
    case DataActionsEnum.BOOT: {
      const newState = action.payload.data.counters;

      return {
        ...newState,
        badge: mergeCounters(state.badge, newState.badge, true),
        badgeUnread: mergeCounters(state.badgeUnread, newState.badgeUnread, true),
        bold: mergeCounters(state.bold, newState.bold, true),
        delegations: mergeWith({}, state.delegations, newState.delegations, mergeCounters),
      };
    }

    case CountersActionsEnum.NOTIFICATIONS_COUNTER_UPDATED:
      return {
        ...state,
        notifications: action.payload,
      };

    case CountersActionsEnum.BADGE_COUNTERS_UPDATED:
      return {
        ...state,
        badge: mergeCounters(state.badge, action.payload.badge),
      };

    case CountersActionsEnum.BADGE_UNREAD_COUNTERS_UPDATED:
      return {
        ...state,
        badgeUnread: mergeCounters(state.badgeUnread, action.payload.badgeUnread),
      };

    case CountersActionsEnum.BOLD_COUNTERS_UPDATED:
      return {
        ...state,
        bold: mergeCounters(state.bold, action.payload.bold),
      };

    case CountersActionsEnum.DELEGATION_COUNTERS_UPDATED: {
      const {delegationId} = action.payload;

      return {
        ...state,
        delegations: {
          ...state.delegations,
          [delegationId]: mergeCounters(state.delegations[delegationId], action.payload.counters),
        },
      };
    }

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function mergeCounters<T extends BadgeCountersModel | BoldCountersModel>(
  state: T | undefined,
  newState: T,
  performVerboseLogging = false,
): T {
  if (!state) {
    return newState;
  }

  const newPartialState: T = {...newState, values: {}};
  const oldGlobalUpdatedTime = state.updatedAt ? new Date(state.updatedAt) : undefined;
  const newGlobalUpdatedTime = newState.updatedAt ? new Date(newState.updatedAt) : undefined;
  const globalUpdatedAtTimeToStore = Math.max(newState.updatedAt ?? 0, state.updatedAt ?? 0);
  for (const key of Object.keys(newState.values)) {
    const oldUidTime = getTimestampFromCounterUid(state.values?.[key]?.uid);
    const newUidTime = getTimestampFromCounterUid(newState.values?.[key]?.uid);

    const oldTime = oldUidTime ?? oldGlobalUpdatedTime;
    const newTime = newUidTime ?? newGlobalUpdatedTime;

    const neitherTimeExists = !oldTime && !newTime;
    const isOnlyNewTimePresent = !oldTime && newTime;
    const isOnlyNewUidPresent = !oldUidTime && newUidTime;
    const isTimeNewer = oldTime && newTime && newTime > oldTime;
    const shouldUpdateCounter =
      neitherTimeExists || isOnlyNewTimePresent || isOnlyNewUidPresent || isTimeNewer;
    if (shouldUpdateCounter) {
      newPartialState.values[key] = newState.values[key];
    }
  }
  const nextState = {
    ...newPartialState,
    values: {
      ...state.values,
      ...newPartialState.values,
    },
    updatedAt: globalUpdatedAtTimeToStore,
  };

  if (performVerboseLogging) {
    auditCounterUpdates(state, newState, nextState);
  }

  return nextState;
}

function auditCounterUpdates<T extends BadgeCountersModel | BoldCountersModel>(
  oldState: T | undefined,
  newState: T,
  nextState: T,
) {
  for (const key of Object.keys(nextState.values)) {
    const nextUid = nextState.values?.[key]?.uid;
    const oldUid = oldState?.values?.[key]?.uid;
    const newUid = newState.values?.[key]?.uid;
    const nextUidTime = getTimestampFromCounterUid(nextUid);
    const oldUidTime = getTimestampFromCounterUid(oldUid);
    const newUidTime = getTimestampFromCounterUid(newUid);

    if (newUidTime && nextUidTime && nextUidTime.getTime() !== newUidTime?.getTime()) {
      logger.warn('Counters UID after merge was stale', {
        nextUidTime: nextUidTime.toISOString(),
        oldUidTime: oldUidTime?.toISOString(),
        newUidTime: newUidTime?.toISOString(),
        nextUid,
        oldUid,
        newUid,
        oldUpdatedAt: oldState?.updatedAt,
        newUpdatedAt: newState.updatedAt,
        nextUpdatedAt: nextState.updatedAt,
      });
    }
  }
}
